import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit-react is in the parent dir
import JoditEditor from 'jodit-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit is in the parent dir
import { IJodit } from 'jodit/esm/types';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  PlusIcon,
  SparklesIcon,
  Truck,
} from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore mustache is in the parent dir
import Mustache from 'mustache';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import {
  SidebarStateContext,
  supportsDraftResponse,
} from 'contexts/sidebarStateContext';
import { isCustomerSupportedTMS, useCustomers } from 'hooks/useCustomers';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getBatchQuote } from 'lib/api/getBatchQuote';
import { LaneHistoryResponse, getLaneHistory } from 'lib/api/getLaneHistory';
import { LaneHistoryFromServiceResponse } from 'lib/api/getLaneHistoryFromService';
import { QuickQuoteResponse } from 'lib/api/getQuickQuote';
import { skipSuggestion } from 'lib/api/skipSuggestion';
import {
  QuoteConfirmationStatus,
  QuoteTerminatingAction,
  QuotedValueTypeApi,
  SubmitFinalQuoteBody,
  updateQuoteRequestSuggestion,
} from 'lib/api/updateQuoteRequestSuggestion';
import { DraftReplyMode } from 'lib/mailclient/interface';
import {
  EMPTY_STOP,
  QuoteCountries,
  SelectedQuoteType,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Email } from 'types/Email';
import { Stop, TransportType } from 'types/QuoteRequest';
import { Maybe, Undef } from 'types/UtilityTypes';
import { BatchQuoteStatus } from 'types/api/BatchQuote';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import { QuoteChanges } from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import {
  copyToClipboard,
  createRichTextContent,
  isHTMLContent,
} from 'utils/copyToClipboard';
import { formatCurrency } from 'utils/formatCurrency';
import { getJoditEditorConfig } from 'utils/getJoditEditorHelpers';
import {
  mapCustomerToSelectOptions,
  normalizeToArray,
} from 'utils/loadInfoAndBuilding';
import { calculatePricing } from 'utils/priceCalculations';
import {
  BatchDefaultProfit,
  resolveDefaultProfit,
  resolveDefaultProfitType,
} from 'utils/quickQuoteDefaults';
import { cn } from 'utils/shadcn';

import { DraftRecipientFields } from '../common/DraftRecipientFields';
import { OutlookQuoteReplyActions } from '../common/OutlookQuoteReplyActions';
import { useDraftRecipients } from '../hooks/useDraftRecipients';
import {
  QuoteReplyAction,
  useQuoteReplyMailClient,
} from '../hooks/useQuoteReplyMailClient';
import {
  BATCH_QUOTE_TEMPLATE,
  BatchEditValues,
  BatchQuoteFormInput,
  BatchQuoteFormValues,
  BatchQuoteResult,
  CarrierCostType,
  ProfitType,
  QuoteFormStop,
  getEquipmentDisplayLabel,
  getParentTransportTypeForSubTransportType,
  getSubTransportTypeOptions,
  getTransportTypeOptions,
} from '../types';
import {
  fetchLaneHistoryFromService,
  formatStopsDisplay,
  parseLocation,
  parseQuoteCardsFromResponse,
} from '../utils';
import { hasValidStructuredLocation } from '../utils/normalizeStopsForQuickQuoteSubmission';
import BatchQuoteEdit from './BatchQuoteEdit';
import { BatchQuoteInputCard } from './BatchQuoteInputCard';

interface ValidQuote {
  index: number;
  quoteData: BatchQuoteFormInput;
  updatedFormValues: BatchQuoteFormInput;
  pickup: QuoteFormStop;
  delivery: QuoteFormStop;
}

function getInvalidStopLabel(stopIdx: number, stopsCount: number): string {
  if (stopIdx === 0) {
    return 'pickup';
  }
  if (stopIdx === stopsCount - 1) {
    return 'delivery';
  }
  return `stop ${stopIdx}`;
}

function getInvalidStopLocationMessage(
  stopIdx: number,
  stopsCount: number
): string {
  if (stopIdx === 0) {
    return 'Invalid pickup location';
  }
  if (stopIdx === stopsCount - 1) {
    return 'Invalid delivery location';
  }
  return `Invalid stop ${stopIdx} location`;
}

interface BatchQuoteFormProps {
  suggestions: GenericSuggestion[];
  email: Maybe<Email>;
}

function formatBatchQuoteStopLocation(
  zip: string,
  city: string,
  state: string
): string {
  if (zip) {
    return zip;
  }
  if (city && state) {
    return `${city}, ${state}`;
  }
  return '';
}

function formatParsedBatchQuoteLocation(
  parsed: ReturnType<typeof parseLocation>
): string {
  if (!parsed) {
    return '';
  }
  if (parsed.zip) {
    return parsed.zip;
  }
  return `${parsed.city}, ${parsed.state}`;
}

const BatchQuoteForm = ({ suggestions, email }: BatchQuoteFormProps) => {
  const { toast } = useToast();
  const {
    quickQuoteConfig,
    configurations: {
      defaultPriceMargin: userDefaultMargin,
      defaultPriceMarginType: userDefaultMarginType,
    },
    subTransportTypes,
    tmsIntegrations,
    serviceID,
    isSharedMailboxUser,
    serviceFeaturesEnabled: {
      isQuoteLaneHistoryEnabled,
      isTMSLaneHistoryEnabled,
      isGetLaneRateFromServiceEnabled,
      isMultiStopQuickQuoteEnabled,
      isQuickQuoteSubTransportTypesEnabled,
    },
  } = useServiceFeatures();

  // Helper to convert plain text to HTML for Jodit
  // - Double newlines (\n\n) = paragraph break with visual spacing
  // - Single newlines (\n) = line break within paragraph (no visual gap)
  const textToHtml = (text: string): string => {
    // Split by double newlines to get paragraphs
    const paragraphs = text.split('\n\n');
    return paragraphs
      .map((para) => {
        // Replace single newlines within paragraph with <br>
        const content = para.trim().replace(/\n/g, '<br>');
        return content ? `<p>${content}</p>` : '';
      })
      .filter(Boolean)
      .join('<p><br></p>'); // Add blank line between paragraphs
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<Maybe<BatchQuoteResult>[]>([]);
  const [laneHistoryResults, setLaneHistoryResults] = useState<
    Maybe<LaneHistoryResponse>[]
  >([]);
  const [laneHistoryFromServiceResults, setLaneHistoryFromServiceResults] =
    useState<Maybe<LaneHistoryFromServiceResponse>[]>([]);
  const [laneSelectedQuickQuoteIds, setLaneSelectedQuickQuoteIds] = useState<
    (number | undefined)[]
  >([]);
  const [selectedLanes, setSelectedLanes] = useState<Set<number>>(new Set());
  const [showBatchEdit, setShowBatchEdit] = useState(false);
  const [expandedLanes, setExpandedLanes] = useState<Set<number>>(new Set());
  const [draftResponse, setDraftResponse] = useState('');
  const [hasCopiedDraftResponse, setHasCopiedDraftResponse] = useState(false);
  const [loadingDraftReply, setLoadingDraftReply] = useState(false);

  const [retryingQuotes, setRetryingQuotes] = useState<Set<number>>(new Set());
  const formContainerRef = useRef<HTMLFormElement>(null);
  const scrollTimeoutRef = useRef<Maybe<NodeJS.Timeout>>(null);
  const draftResponseJoditRef = useRef<Maybe<IJodit>>(null);
  const userEditedDraftRef = useRef(false);

  const {
    currentState: {
      drumkitPlatform,
      threadId,
      threadItemId,
      isOutlookReply,
      isSharedMailboxEmail,
      inboxEmailAddress,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);
  const isSharedMailbox =
    (isSharedMailboxUser ?? false) || (isSharedMailboxEmail ?? false);

  const {
    to: draftToRecipients,
    cc: draftCc,
    bcc: draftBcc,
    setTo: setDraftTo,
    setCc: setDraftCc,
    setBcc: setDraftBcc,
  } = useDraftRecipients();
  const { canDraftReply, canSendReply, replyWithMailClient } =
    useQuoteReplyMailClient();

  const markUserEditedDraft = useCallback(() => {
    userEditedDraftRef.current = true;
  }, []);

  const resetUserEditedDraft = useCallback(() => {
    userEditedDraftRef.current = false;
  }, []);

  const getBatchDefaultProfit = useCallback((): BatchDefaultProfit => {
    const defaultProfitType = resolveDefaultProfitType({
      inboxEmailAddress,
      userDefaultMarginType,
      serviceDefaultMarginType: quickQuoteConfig?.defaultMarginType,
    });
    const defaultProfit = resolveDefaultProfit({
      inboxEmailAddress,
      userDefaultMargin,
      userDefaultMarginType,
      serviceDefaultMarginType: quickQuoteConfig?.defaultMarginType,
      serviceDefaultFlatMargin: quickQuoteConfig?.defaultFlatMargin,
      serviceDefaultPercentMargin: quickQuoteConfig?.defaultPercentMargin,
    });

    const batchDefaults: BatchDefaultProfit = {
      defaultProfitType,
      defaultProfit,
    };
    return batchDefaults;
  }, [
    inboxEmailAddress,
    userDefaultMargin,
    userDefaultMarginType,
    quickQuoteConfig,
  ]);

  const [lastBatchEditMargin, setLastBatchEditMargin] = useState(() => {
    const { defaultProfitType, defaultProfit } = getBatchDefaultProfit();

    return {
      margin: defaultProfit.toString(),
      marginType: defaultProfitType,
    };
  });
  const batchMarginBeforeInboxHydrationRef = useRef(lastBatchEditMargin);
  const syncedInboxForBatchDefaultsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!inboxEmailAddress) {
      return;
    }

    const previousInbox = syncedInboxForBatchDefaultsRef.current;
    syncedInboxForBatchDefaultsRef.current = inboxEmailAddress;

    if (previousInbox === null || previousInbox === '') {
      const snapshot = batchMarginBeforeInboxHydrationRef.current;
      if (
        lastBatchEditMargin.margin === snapshot.margin &&
        lastBatchEditMargin.marginType === snapshot.marginType
      ) {
        const { defaultProfitType, defaultProfit } = getBatchDefaultProfit();
        setLastBatchEditMargin({
          margin: defaultProfit.toString(),
          marginType: defaultProfitType,
        });
      }
    }
  }, [
    inboxEmailAddress,
    getBatchDefaultProfit,
    lastBatchEditMargin.margin,
    lastBatchEditMargin.marginType,
  ]);

  const transportTypeOptions = useMemo(
    () => getTransportTypeOptions(quickQuoteConfig),
    [quickQuoteConfig]
  );

  const subTransportTypeOptions = useMemo(
    () => getSubTransportTypeOptions(subTransportTypes),
    [subTransportTypes]
  );

  const useGranularEquipmentSelect =
    isQuickQuoteSubTransportTypesEnabled && subTransportTypeOptions.length > 0;

  const createEmptyQuote = (): BatchQuoteFormInput => {
    const { defaultProfitType, defaultProfit } = getBatchDefaultProfit();
    return {
      transportType: TransportType.VAN,
      stops: [
        { ...EMPTY_STOP, order: 0 },
        { ...EMPTY_STOP, order: 1 },
      ],
      pickupDate: new Date(),
      pickupCloseDate: null,
      deliveryDate: new Date(),
      deliveryCloseDate: null,
      isSubmitToTMS: false,
      customerName: '',
      quoteNumber: '',
      quoteExpirationDate: new Date(),
      quoteEta: new Date(),
      profit: defaultProfit,
      profitType: defaultProfitType,
    };
  };

  const memoizedDefaultValues = useMemo<BatchQuoteFormValues>(() => {
    const defaultCustomerName = suggestions.length
      ? ((suggestions[0].suggested as QuoteChanges)?.customerExternalTMSID ??
        '')
      : '';

    if (suggestions.length === 0) {
      return { customerName: defaultCustomerName, quotes: [] };
    }

    const { defaultProfitType, defaultProfit } = getBatchDefaultProfit();

    const buildStopFromSuggestion = (
      stop: Stop,
      order: number
    ): QuoteFormStop => {
      const city = stop.address?.city ?? '';
      const state = stop.address?.state ?? '';
      const zip = stop.address?.zip ?? '';
      const country = (stop.address?.country as QuoteCountries) ?? '';
      const location = formatBatchQuoteStopLocation(zip, city, state);
      const externalTMSID = stop.externalTMSID?.trim() ?? '';

      return {
        order,
        location,
        city,
        state,
        zip,
        country,
        addressName: stop.address?.name ?? '',
        addressLine1: stop.address?.addressLine1 ?? '',
        addressLine2: stop.address?.addressLine2 ?? '',
        ...(externalTMSID ? { externalTMSID } : {}),
      };
    };

    const buildStopFromLegacyFields = (
      order: number,
      isPickup: boolean,
      suggestedFields: QuoteChanges
    ): QuoteFormStop => {
      const raw = (() => {
        if (isPickup) {
          if (suggestedFields?.pickupZip) {
            return suggestedFields.pickupZip;
          }
          return `${suggestedFields?.pickupCity}, ${suggestedFields?.pickupState}`;
        }
        if (suggestedFields?.deliveryZip) {
          return suggestedFields.deliveryZip;
        }
        return `${suggestedFields?.deliveryCity}, ${suggestedFields?.deliveryState}`;
      })();
      const parsed = parseLocation(raw);
      return {
        order,
        location: formatParsedBatchQuoteLocation(parsed),
        city: parsed?.city ?? '',
        state: parsed?.state ?? '',
        zip: parsed?.zip ?? '',
        country: (parsed?.country as QuoteCountries) ?? ('' as QuoteCountries),
      };
    };

    const defaultQuotes = suggestions.map((suggestion) => {
      const suggestedFields = suggestion.suggested as QuoteChanges;
      const suggestedStops = normalizeToArray(suggestedFields?.stops) ?? [];
      const hasSuggestedMultiStops = suggestedStops.length > 2;

      let stops: QuoteFormStop[];
      if (isMultiStopQuickQuoteEnabled && hasSuggestedMultiStops) {
        stops = suggestedStops.map((stop, idx) =>
          buildStopFromSuggestion(stop, idx)
        );
      } else if (isMultiStopQuickQuoteEnabled && suggestedStops.length >= 2) {
        // Fall back to first/last stop when the multi-stop UI is enabled but
        // the suggestion only provides two stops anyway.
        stops = [
          buildStopFromSuggestion(suggestedStops[0], 0),
          buildStopFromSuggestion(suggestedStops[suggestedStops.length - 1], 1),
        ];
      } else {
        stops = [
          buildStopFromLegacyFields(0, true, suggestedFields),
          buildStopFromLegacyFields(1, false, suggestedFields),
        ];
      }

      return {
        transportType: suggestedFields?.transportType ?? '',
        stops,
        pickupDate: new Date(),
        pickupCloseDate: null,
        deliveryDate: new Date(),
        deliveryCloseDate: null,
        isSubmitToTMS: false,
        customerName: defaultCustomerName,
        quoteNumber: '',
        quoteExpirationDate: new Date(),
        quoteEta: new Date(),
        profit: defaultProfit,
        profitType: defaultProfitType,
        suggestionId: suggestion.id,
      } as BatchQuoteFormInput;
    });

    return { customerName: defaultCustomerName, quotes: defaultQuotes };
  }, [suggestions, isMultiStopQuickQuoteEnabled, getBatchDefaultProfit]);

  const formMethods = useForm<BatchQuoteFormValues>({
    defaultValues: memoizedDefaultValues,
    reValidateMode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = formMethods;

  // Watch form values to trigger draft updates when margins change
  const watchedQuotes = useWatch({
    control,
    name: 'quotes',
  });

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'quotes',
  });

  const quoteLaneCount = fields.length;

  // Watch for customerName changes and propagate will be handled later after fields are defined
  const watchedCustomerName = useWatch({ control, name: 'customerName' });

  // Propagate customer to all lanes whenever it changes or lane count changes.
  useEffect(() => {
    if (watchedCustomerName === undefined) {
      return;
    }

    for (let idx = 0; idx < quoteLaneCount; idx++) {
      setValue(`quotes.${idx}.customerName`, watchedCustomerName);
    }
  }, [watchedCustomerName, quoteLaneCount, setValue]);

  // For Posthog Button Events
  const logProperties = useMemo(() => {
    return {
      serviceID,
      threadID: email?.threadID,
    };
  }, [serviceID, email?.threadID]);

  const handleAddLane = () => {
    append(createEmptyQuote());

    // Propagate selected customer to the new lane
    const currentCustomer = getValues('customerName');
    const newIndex = fields.length; // index after append
    if (currentCustomer) {
      setValue(`quotes.${newIndex}.customerName`, currentCustomer);
    }

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Smoothly scroll the nearest scrollable container instead of relying on
    // element.scrollIntoView which can pick the wrong ancestor and cause the
    // viewport to appear "locked" in some hosts (e.g., Gmail wrappers).
    const findScrollableParent = (
      element: Maybe<HTMLElement>
    ): Maybe<HTMLElement> => {
      if (!element) return null;
      let current: Maybe<HTMLElement> = element.parentElement;
      while (current) {
        const style = window.getComputedStyle(current);
        const isScrollable =
          current.scrollHeight > current.clientHeight &&
          (style.overflowY === 'auto' ||
            style.overflowY === 'scroll' ||
            style.overflowY === 'overlay');
        if (isScrollable) return current;
        current = current.parentElement;
      }
      return null;
    };

    scrollTimeoutRef.current = setTimeout(() => {
      const container = formContainerRef.current;
      if (!container) {
        scrollTimeoutRef.current = null;
        return;
      }

      const scrollable = findScrollableParent(container);
      if (scrollable) {
        scrollable.scrollTo({
          top: scrollable.scrollHeight,
          behavior: 'smooth',
        });
      }
      scrollTimeoutRef.current = null;
    }, 100);
  };

  const handleSelectLane = (index: number, isSelected: boolean) => {
    setSelectedLanes((prev) => {
      const newSelected = new Set(prev);
      if (isSelected) {
        newSelected.add(index);
      } else {
        newSelected.delete(index);
        if (newSelected.size === 0) {
          setShowBatchEdit(false);
        }
      }
      return newSelected;
    });
  };

  const handleToggleExpandLane = (index: number) => {
    setExpandedLanes((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  };

  const handleSelectAll = () => {
    if (selectedLanes.size === fields.length) {
      setSelectedLanes(new Set());
      setShowBatchEdit(false);
    } else {
      setSelectedLanes(new Set(fields.map((_, index) => index)));
    }
  };

  const handleInitiateBatchEdit = () => {
    setShowBatchEdit(true);
  };

  const handleCancelBatchEdit = () => {
    setShowBatchEdit(false);
  };

  const handleApplyBatchEdit = (batchValues: BatchEditValues) => {
    setLastBatchEditMargin({
      margin: batchValues.margin,
      marginType: batchValues.marginType,
    });

    selectedLanes.forEach((index) => {
      if (useGranularEquipmentSelect && batchValues.subTransportType) {
        setValue(
          `quotes.${index}.subTransportType`,
          batchValues.subTransportType
        );
        const parentTransportType = getParentTransportTypeForSubTransportType(
          batchValues.subTransportType,
          subTransportTypeOptions
        );
        if (parentTransportType) {
          setValue(`quotes.${index}.transportType`, parentTransportType);
        }
      } else if (batchValues.transportType) {
        setValue(
          `quotes.${index}.transportType`,
          batchValues.transportType as TransportType
        );
      }

      // Bulk pickup/dropoff only makes sense for two-stop lanes. Multi-stop
      // lanes edit geo inline on each card because the middle stops are
      // lane-specific.
      if (!isMultiStopQuickQuoteEnabled && batchValues.pickupLocation) {
        const parsedPickup = parseLocation(batchValues.pickupLocation);
        if (parsedPickup) {
          setValue(
            `quotes.${index}.stops.0.location`,
            batchValues.pickupLocation
          );
          setValue(`quotes.${index}.stops.0.city`, parsedPickup.city);
          setValue(`quotes.${index}.stops.0.state`, parsedPickup.state);
          setValue(`quotes.${index}.stops.0.zip`, parsedPickup.zip);
          setValue(`quotes.${index}.stops.0.country`, parsedPickup.country);
        }
      }

      if (!isMultiStopQuickQuoteEnabled && batchValues.dropoffLocation) {
        const parsedDropoff = parseLocation(batchValues.dropoffLocation);
        if (parsedDropoff) {
          setValue(
            `quotes.${index}.stops.1.location`,
            batchValues.dropoffLocation
          );
          setValue(`quotes.${index}.stops.1.city`, parsedDropoff.city);
          setValue(`quotes.${index}.stops.1.state`, parsedDropoff.state);
          setValue(`quotes.${index}.stops.1.zip`, parsedDropoff.zip);
          setValue(`quotes.${index}.stops.1.country`, parsedDropoff.country);
        }
      }

      if (batchValues.margin) {
        setValue(`quotes.${index}.profit`, parseFloat(batchValues.margin));
        setValue(`quotes.${index}.profitType`, batchValues.marginType);
      }
    });

    // Apply buy rate source to selected lanes via registered setters
    if (batchValues.buyRateSource) {
      selectedLanes.forEach((index) => {
        const setter = carrierSetterMapRef.current.get(index);
        if (setter) {
          setter(batchValues.buyRateSource as SelectedQuoteType);
        }
      });
    }

    // Apply price format to selected lanes via registered setters
    if (batchValues.priceFormat) {
      selectedLanes.forEach((index) => {
        const setter = priceFormatSetterMapRef.current.get(index);
        if (setter) {
          setter(batchValues.priceFormat as CarrierCostType | 'Both');
          // Also update our tracking ref
          laneCurrentPriceFormatsRef.current.set(
            index,
            batchValues.priceFormat as CarrierCostType | 'Both'
          );
        }
      });
    }

    setShowBatchEdit(false);
  };

  const handleRemoveQuote = useCallback(
    async (index: number) => {
      const removedSuggestionId = getValues(`quotes.${index}.suggestionId`);

      if (removedSuggestionId != null) {
        const result = await skipSuggestion(removedSuggestionId);
        if (result.isErr()) {
          toast({
            description: result.error.message,
            variant: 'destructive',
          });
          return;
        }
      }

      remove(index);

      if (removedSuggestionId != null) {
        setCurrentState((prev) => ({
          ...prev,
          batchSelectedSuggestionIds: (
            prev.batchSelectedSuggestionIds ?? []
          ).filter((id) => id !== removedSuggestionId),
          curSuggestionList: prev.curSuggestionList.map((suggestion) =>
            suggestion.id === removedSuggestionId
              ? { ...suggestion, status: SuggestionStatus.Rejected }
              : suggestion
          ),
          clickedSuggestion:
            prev.clickedSuggestion?.id === removedSuggestionId
              ? null
              : prev.clickedSuggestion,
        }));
      }

      setResults((prev) => {
        const newResults = [...prev];
        newResults.splice(index, 1);
        return newResults;
      });

      setLaneHistoryResults((prev) => {
        const newResults = [...prev];
        newResults.splice(index, 1);
        return newResults;
      });

      // Clear any retrying state for this quote
      setRetryingQuotes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });

      // Update expanded lanes, reindexing as needed
      setExpandedLanes((prev) => {
        const newExpanded = new Set<number>();
        prev.forEach((expandedIdx) => {
          if (expandedIdx < index) {
            newExpanded.add(expandedIdx);
          } else if (expandedIdx > index) {
            newExpanded.add(expandedIdx - 1);
          }
        });
        return newExpanded;
      });

      // Clean up Map references
      carrierSetterMapRef.current.delete(index);
      laneCalculatedPricesRef.current.delete(index);
      priceFormatSetterMapRef.current.delete(index);
      laneCurrentPriceFormatsRef.current.delete(index);

      // Reindex remaining Map entries
      const carrierSetters = new Map<
        number,
        (carrier: SelectedQuoteType) => void
      >();
      const laneCalculatedPrices = new Map<
        number,
        {
          finalPrice: number;
          maxDistance: number;
          fuelEstimate: number;
        }
      >();
      const priceFormatSetters = new Map<
        number,
        (priceFormat: CarrierCostType | 'Both') => void
      >();
      const laneCurrentPriceFormats = new Map<
        number,
        CarrierCostType | 'Both'
      >();

      carrierSetterMapRef.current.forEach((setter, mapIndex) => {
        if (mapIndex > index) {
          carrierSetters.set(mapIndex - 1, setter);
        } else if (mapIndex < index) {
          carrierSetters.set(mapIndex, setter);
        }
      });

      laneCalculatedPricesRef.current.forEach((price, mapIndex) => {
        if (mapIndex > index) {
          laneCalculatedPrices.set(mapIndex - 1, price);
        } else if (mapIndex < index) {
          laneCalculatedPrices.set(mapIndex, price);
        }
      });

      priceFormatSetterMapRef.current.forEach((setter, mapIndex) => {
        if (mapIndex > index) {
          priceFormatSetters.set(mapIndex - 1, setter);
        } else if (mapIndex < index) {
          priceFormatSetters.set(mapIndex, setter);
        }
      });

      laneCurrentPriceFormatsRef.current.forEach((format, mapIndex) => {
        if (mapIndex > index) {
          laneCurrentPriceFormats.set(mapIndex - 1, format);
        } else if (mapIndex < index) {
          laneCurrentPriceFormats.set(mapIndex, format);
        }
      });

      carrierSetterMapRef.current = carrierSetters;
      laneCalculatedPricesRef.current = laneCalculatedPrices;
      priceFormatSetterMapRef.current = priceFormatSetters;
      laneCurrentPriceFormatsRef.current = laneCurrentPriceFormats;
    },
    [remove, getValues, setCurrentState, toast]
  );

  const handleRetryQuote = useCallback(
    async (index: number) => {
      // Set loading state for this quote
      setRetryingQuotes((prev) => new Set(prev).add(index));

      try {
        const startTime = Date.now();
        const quoteData = getValues(`quotes.${index}`);

        // Clear existing error for this quote
        formMethods.clearErrors(`quotes.${index}.root` as any);

        // Validate every stop for multi-stop support. Each stop's location
        // must parse to a real city/state/zip.
        const stopsCount = quoteData.stops.length;
        const parsedStops = quoteData.stops.map((stop) =>
          hasValidStructuredLocation(stop)
            ? stop
            : parseLocation(stop.location || '')
        );
        let hasInvalidStop = false;
        parsedStops.forEach((parsed, stopIdx) => {
          if (!parsed) {
            hasInvalidStop = true;
            const label = getInvalidStopLabel(stopIdx, stopsCount);
            formMethods.setError(
              `quotes.${index}.stops.${stopIdx}.location` as any,
              { message: `Invalid ${label} location` }
            );
          }
        });

        if (hasInvalidStop) {
          setRetryingQuotes((prev) => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
          });
          return;
        }

        // Enforce granular equipment selection on retry too, mirroring onSubmit.
        // Without this, a retry would send the parent transportType with no
        // subTransportType and mis-tag the lane for Swick-style analytics.
        if (useGranularEquipmentSelect && !quoteData.subTransportType) {
          formMethods.setError(`quotes.${index}.root` as any, {
            message: 'Select equipment for this lane',
          });
          setRetryingQuotes((prev) => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
          });
          return;
        }

        const mappedStops: QuoteFormStop[] = parsedStops.map(
          (parsed, stopIdx) => ({
            ...(hasValidStructuredLocation(quoteData.stops[stopIdx])
              ? quoteData.stops[stopIdx]
              : {}),
            order: stopIdx,
            zip: parsed!.zip,
            city: parsed!.city,
            state: parsed!.state,
            country: parsed!.country as QuoteCountries,
            location: quoteData.stops[stopIdx].location,
          })
        );

        const pickup = mappedStops[0];
        const delivery = mappedStops[mappedStops.length - 1];

        const updatedFormValues: BatchQuoteFormInput = {
          ...quoteData,
          stops: mappedStops,
        };

        const batchResponse = await getBatchQuote({
          isMultiStopQuickQuoteEnabled,
          email: email,
          threadId,
          clickedSuggestion: (() => {
            const suggestionId = quoteData.suggestionId;
            const suggestion =
              (suggestionId != null
                ? suggestions.find((s) => s.id === suggestionId)
                : undefined) ?? suggestions[index];
            return suggestion ? [suggestion] : [];
          })(),
          formValues: {
            customerName: getValues('customerName'),
            quotes: [updatedFormValues],
          },
          formMethods: formMethods,
          setQuoteNotConfidentHandler: () => {},
          setProfit: () => {},
          profitType: quoteData.profitType || ProfitType.Percentage,
        });

        if (!batchResponse || !batchResponse.results[0]) {
          return;
        }

        const result = batchResponse.results[0];

        if (result.status === BatchQuoteStatus.Success && result.quotes) {
          // Convert BatchQuoteResult to the expected format
          const response: QuickQuoteResponse = {
            quoteRequestId: result.quoteRequestId,
            stops: result.stops || [],
            selectedRateName:
              result.selectedRateName || SelectedQuoteType.GS_NETWORK,
            inputtedTransportType:
              result.inputtedTransportType || updatedFormValues.transportType,
            submittedTransportType:
              result.submittedTransportType || updatedFormValues.transportType,
            configuration: result.configuration,
            quoteReplyDraftTemplate: result.quoteReplyDraftTemplate || {
              subject: '',
              body: '',
            },
            quotes: result.quotes,
            isZipCodeLookup: result.isZipCodeLookup || false,
          };

          const cards = parseQuoteCardsFromResponse({
            newQuote: response,
            formValues: updatedFormValues,
            setDATFuelSurcharge: () => {},
          });

          // Update the results for this specific index
          setResults((prev) => {
            const newResults = [...prev];
            newResults[index] = { response, cards };
            return newResults;
          });

          // Try to fetch lane history
          if (
            (isQuoteLaneHistoryEnabled || isTMSLaneHistoryEnabled) &&
            result.quoteRequestId
          ) {
            try {
              const laneHistoryResponse = await getLaneHistory({
                quoteRequestId: result.quoteRequestId,
                originCity: pickup.city,
                originState: pickup.state,
                originZip: pickup.zip,
                originCountry: pickup.country ?? QuoteCountries.USA,
                destinationCity: delivery.city,
                destinationState: delivery.state,
                destinationZip: delivery.zip,
                destinationCountry: delivery.country ?? QuoteCountries.USA,
                transportType: updatedFormValues.transportType,
              });

              if (laneHistoryResponse.isOk()) {
                setLaneHistoryResults((prev) => {
                  const newResults = [...prev];
                  newResults[index] = laneHistoryResponse.value;
                  return newResults;
                });
              }
            } catch (error) {
              captureException(error, {
                functionName: 'retryQuote_getLaneHistory',
              });
            }
          }

          toast({
            description: 'Quote retry successful!',
            variant: 'success',
          });
        } else if (result.status === BatchQuoteStatus.Error) {
          // Handle error case
          if (result.error) {
            formMethods.setError(`quotes.${index}.root` as any, {
              message: result.error,
            });
          }
        }

        const elapsed = Date.now() - startTime;
        if (elapsed < 500) {
          await new Promise((resolve) => setTimeout(resolve, 500 - elapsed));
        }
      } catch (err) {
        captureException(err, {
          functionName: 'handleRetryQuote',
        });
        toast({
          description: 'Unable to retry quote.',
          variant: 'destructive',
        });
      } finally {
        // Clear loading state for this quote
        setRetryingQuotes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }
    },
    [
      getValues,
      formMethods,
      email,
      threadId,
      suggestions,
      isQuoteLaneHistoryEnabled,
      isTMSLaneHistoryEnabled,
      isMultiStopQuickQuoteEnabled,
      useGranularEquipmentSelect,
      toast,
    ]
  );

  const onSubmit = handleSubmit(async (data: BatchQuoteFormValues) => {
    setIsSubmitting(true);
    setResults([]);
    setLaneHistoryResults([]);
    setLaneHistoryFromServiceResults([]);
    setRetryingQuotes(new Set());

    // Clear any existing form errors for individual quotes
    data.quotes.forEach((_, index) => {
      formMethods.clearErrors(`quotes.${index}.root` as any);
    });

    // Validate all quotes upfront and prepare batch request
    const validQuotes: ValidQuote[] = [];

    data.quotes.forEach((quoteData, index) => {
      const stopsCount = quoteData.stops.length;
      const parsedStops = quoteData.stops.map((stop) =>
        hasValidStructuredLocation(stop)
          ? stop
          : parseLocation(stop.location || '')
      );

      let hasInvalidStop = false;
      parsedStops.forEach((parsed, stopIdx) => {
        if (!parsed) {
          hasInvalidStop = true;
          formMethods.setError(
            `quotes.${index}.stops.${stopIdx}.location` as any,
            {
              message: getInvalidStopLocationMessage(stopIdx, stopsCount),
            }
          );
        }
      });

      if (hasInvalidStop) {
        return;
      }

      if (useGranularEquipmentSelect && !quoteData.subTransportType) {
        formMethods.setError(`quotes.${index}.root` as any, {
          message: 'Select equipment for this lane',
        });
        return;
      }

      // Reflect parsed location components into form state so the input
      // cards render canonical city/state/zip info.
      parsedStops.forEach((parsed, stopIdx) => {
        setValue(`quotes.${index}.stops.${stopIdx}.city`, parsed!.city);
        setValue(`quotes.${index}.stops.${stopIdx}.state`, parsed!.state);
        setValue(`quotes.${index}.stops.${stopIdx}.zip`, parsed!.zip);
        setValue(
          `quotes.${index}.stops.${stopIdx}.country`,
          parsed!.country as QuoteCountries
        );
      });

      const mappedStops: QuoteFormStop[] = parsedStops.map(
        (parsed, stopIdx) => ({
          ...(hasValidStructuredLocation(quoteData.stops[stopIdx])
            ? quoteData.stops[stopIdx]
            : {}),
          order: stopIdx,
          zip: parsed!.zip,
          city: parsed!.city,
          state: parsed!.state,
          country: parsed!.country as QuoteCountries,
          location: quoteData.stops[stopIdx].location,
        })
      );

      const pickup = mappedStops[0];
      const delivery = mappedStops[mappedStops.length - 1];

      const updatedFormValues: BatchQuoteFormInput = {
        ...quoteData,
        stops: mappedStops,
      };

      validQuotes.push({
        index,
        quoteData,
        updatedFormValues,
        pickup,
        delivery,
      });
    });

    if (validQuotes.length === 0) {
      setIsSubmitting(false);
      return;
    }

    const batchResponse = await getBatchQuote({
      isMultiStopQuickQuoteEnabled,
      email: email,
      threadId,
      clickedSuggestion: validQuotes.map((vq) => {
        const suggestionId = vq.updatedFormValues.suggestionId;
        return (
          (suggestionId != null
            ? suggestions.find((s) => s.id === suggestionId)
            : undefined) ?? suggestions[vq.index]
        );
      }),
      formValues: {
        customerName: data.customerName,
        quotes: validQuotes.map((vq) => vq.updatedFormValues),
      },
      formMethods: formMethods,
      setQuoteNotConfidentHandler: () => {},
      setProfit: () => {},
      profitType: validQuotes[0]?.quoteData.profitType || ProfitType.Percentage,
    });

    if (!batchResponse) {
      toast({
        description: 'Unable to get batch quotes.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    // Show error toast if all results are errors (full batch failure)
    const allFailed = batchResponse.results.every(
      (result: (typeof batchResponse.results)[number]) =>
        result.status === BatchQuoteStatus.Error
    );
    if (allFailed) {
      toast({
        description:
          'All batch quotes failed. Please check your input and try again.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    // Process batch response and fetch lane history for successful quotes
    const processedResults: Maybe<BatchQuoteResult>[] = Array(
      data.quotes.length
    ).fill(null);
    const processedLaneHistoryResults: Maybe<LaneHistoryResponse>[] = Array(
      data.quotes.length
    ).fill(null);
    const processedLaneHistoryFromServiceResults: Maybe<LaneHistoryFromServiceResponse>[] =
      Array(data.quotes.length).fill(null);

    const laneHistoryPromises = batchResponse.results.map(
      async (result, resultIndex) => {
        const validQuote = validQuotes[resultIndex];
        if (!validQuote) return;

        const { index, updatedFormValues, pickup, delivery } = validQuote;

        if (result.status === BatchQuoteStatus.Success && result.quotes) {
          // Convert BatchQuoteResult to the expected format
          const response = {
            quoteRequestId: result.quoteRequestId,
            stops: result.stops || [],
            selectedRateName:
              result.selectedRateName || SelectedQuoteType.GS_NETWORK,
            inputtedTransportType:
              result.inputtedTransportType || updatedFormValues.transportType,
            submittedTransportType:
              result.submittedTransportType || updatedFormValues.transportType,
            configuration: result.configuration,
            quoteReplyDraftTemplate: result.quoteReplyDraftTemplate || {
              subject: '',
              body: '',
            },
            quotes: result.quotes,
            isZipCodeLookup: result.isZipCodeLookup || false,
          };

          const cards = parseQuoteCardsFromResponse({
            newQuote: response,
            formValues: updatedFormValues,
            setDATFuelSurcharge: () => {},
          });

          processedResults[index] = { response, cards };

          // Fetch lane history if enabled and we have a valid quote request ID
          if (
            (isQuoteLaneHistoryEnabled || isTMSLaneHistoryEnabled) &&
            result.quoteRequestId
          ) {
            try {
              const laneHistoryResponse = await getLaneHistory({
                quoteRequestId: result.quoteRequestId,
                originCity: pickup.city,
                originState: pickup.state,
                originZip: pickup.zip,
                originCountry: pickup.country || QuoteCountries.USA,
                destinationCity: delivery.city,
                destinationState: delivery.state,
                destinationZip: delivery.zip,
                destinationCountry: delivery.country || QuoteCountries.USA,
                transportType: updatedFormValues.transportType,
              });

              if (laneHistoryResponse.isOk()) {
                processedLaneHistoryResults[index] = laneHistoryResponse.value;
              }
            } catch (error) {
              captureException(error, {
                functionName: 'batchQuote_getLaneHistory',
              });
            }
          }

          // Fetch lane history from service if enabled
          if (isGetLaneRateFromServiceEnabled && result.quoteRequestId) {
            try {
              const laneHistoryResponse = await fetchLaneHistoryFromService({
                quoteRequestId: result.quoteRequestId,
                originCity: pickup.city,
                originState: pickup.state,
                originZip: pickup.zip,
                originCountry: pickup.country || QuoteCountries.USA,
                destinationCity: delivery.city,
                destinationState: delivery.state,
                destinationZip: delivery.zip,
                destinationCountry: delivery.country || QuoteCountries.USA,
                transportType: updatedFormValues.transportType,
              });

              if (laneHistoryResponse.isOk()) {
                processedLaneHistoryFromServiceResults[index] =
                  laneHistoryResponse.value;
              }
            } catch (error) {
              captureException(error, {
                functionName: 'batchQuote_getLaneHistoryFromService',
              });
            }
          }
        } else if (result.status === BatchQuoteStatus.Error) {
          // Handle individual quote errors
          if (result.error) {
            formMethods.setError(`quotes.${index}.root` as any, {
              message: result.error,
            });
          }
        }
      }
    );

    await Promise.all(laneHistoryPromises);

    setResults(processedResults as Maybe<BatchQuoteResult>[]);
    setLaneHistoryResults(processedLaneHistoryResults);
    setLaneHistoryFromServiceResults(processedLaneHistoryFromServiceResults);
    setIsSubmitting(false);
  });

  const isAllSelected =
    fields.length > 0 && selectedLanes.size === fields.length;
  const hasSelection = selectedLanes.size > 0;
  const isSubmitButtonDisabled = isSubmitting || fields.length === 0;

  // Number of lanes that have results and are expandable
  const expandableLanesCount = useMemo(
    () => results.filter((r) => r).length,
    [results]
  );
  const allLanesExpanded =
    expandableLanesCount > 0 && expandedLanes.size === expandableLanesCount;

  const handleToggleExpandAll = () => {
    if (allLanesExpanded) {
      setExpandedLanes(new Set());
    } else {
      const allResultIndices = results
        .map((result, index) => (result ? index : -1))
        .filter((index) => index !== -1);
      setExpandedLanes(new Set(allResultIndices));
    }
  };

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  // Format price based on individual lane's selected format
  const formatPriceForDraft = (
    finalPrice: number,
    maxDistance: number,
    fuelEstimate: number,
    priceFormat: CarrierCostType | 'Both'
  ): string => {
    if (priceFormat === CarrierCostType.Linehaul && fuelEstimate) {
      return formatCurrency(finalPrice - fuelEstimate, 'USD');
    }

    if (!maxDistance) return formatCurrency(finalPrice, 'USD');

    if (priceFormat === 'Both') {
      return `${formatCurrency(finalPrice, 'USD')} (${formatCurrency(
        finalPrice / maxDistance,
        'USD',
        2
      )}/mi)`;
    } else if (priceFormat === CarrierCostType.PerMile) {
      return `${formatCurrency(finalPrice / maxDistance, 'USD', 2)}/mi`;
    } else {
      return formatCurrency(finalPrice, 'USD');
    }
  };

  // Reset user edited flag when results change (new quotes generated)
  useEffect(() => {
    resetUserEditedDraft();
  }, [results, resetUserEditedDraft]);

  const onSelectedQuoteIdChange = useCallback(
    (laneIndex: number, quoteId: number | undefined) => {
      setLaneSelectedQuickQuoteIds((prev) => {
        const next = [...prev];
        next[laneIndex] = quoteId;
        return next;
      });
    },
    []
  );

  const onLaneTerminatingAction = useCallback(
    async (params: {
      quoteRequestId: number;
      finalQuoteData: SubmitFinalQuoteBody;
      selectedQuickQuoteId?: number;
      action: QuoteTerminatingAction;
      suggestionId?: number;
      quotedValueType?: Undef<QuotedValueTypeApi>;
    }) => {
      const result = await updateQuoteRequestSuggestion(
        params.quoteRequestId,
        SuggestionStatus.Accepted,
        {
          ...params.finalQuoteData,
          quoteConfirmationStatus: QuoteConfirmationStatus.UserConfirmed,
          quoteTerminatingAction: params.action,
          quotedValueType: params.quotedValueType,
        },
        params.selectedQuickQuoteId
      );
      if (result.isErr()) {
        captureException(result.error, {
          functionName: 'onLaneTerminatingAction',
        });
        return;
      }

      // Prefer Kit suggestion id so Done matches the pill even if the quoteRequestId diverges.
      const completedKitId = params.suggestionId ?? params.quoteRequestId;
      setCurrentState((prevState) => ({
        ...prevState,
        ...applyCompleteKitSuggestions(
          prevState,
          completedKitId,
          SuggestionPipelines.QuickQuote
        ),
      }));
    },
    [setCurrentState]
  );

  // handleTerminatingAction should be called whenever user takes an action that "terminates" the quote requests,
  // i.e. updates the QR status from "inFlight" to 1 of 2 terminating statuses: "accepted" or "rejected".
  // As of 6/4/2025, this occurs when user clicks "Create Draft Reply" or one of the copy buttons.
  // This function
  // 1) Updates BE quote request with final quote and margin values (critical for metrics)
  // 2) Sends user quote to service (if enabled)
  // 3) Updates UI to show success or failure toast
  const handleTerminatingAction = async (
    terminatingAction: QuoteTerminatingAction = QuoteTerminatingAction.Copy
  ) => {
    try {
      // Process each quote result that has completed
      const quotesWithResults = results
        .map((result, index) => {
          if (!result?.response) return null;

          const formData = getValues(`quotes.${index}`);
          const suggestionId = formData.suggestionId;
          const suggestion =
            (suggestionId != null
              ? suggestions.find((s) => s.id === suggestionId)
              : undefined) ?? suggestions[index];

          // Get the carrier cost from the cheapest card (same logic as individual cards)
          const carrierCost = result.cards?.length
            ? result.cards.reduce((prev, curr) =>
                prev.cost < curr.cost ? prev : curr
              ).cost
            : 0;

          const maxDistance =
            result.response.quotes?.reduce(
              (max, current) =>
                current.distance > max.distance ? current : max,
              { distance: 0 }
            ).distance || 0;

          const profit = formData.profit || 0;
          const profitType = formData.profitType || ProfitType.Percentage;

          // Use the same price calculation logic as the individual cards
          const { flatCarrierCost, finalProfit } = calculatePricing(
            carrierCost,
            CarrierCostType.Flat, // For terminating action calculation, use flat carrier cost
            profit,
            profitType,
            maxDistance
          );

          const finalPrice = flatCarrierCost + finalProfit;

          return {
            suggestion,
            result,
            formData,
            carrierCost: flatCarrierCost,
            profit: finalProfit,
            finalPrice,
            profitType,
            parentQuoteRequestId: result.response.quoteRequestId || 0,
            laneIndex: index,
          };
        })
        .filter(Boolean);

      // Update each quote request suggestion
      const updatePromises = quotesWithResults.map(async (quote) => {
        if (!quote) return;

        const result = await updateQuoteRequestSuggestion(
          quote.parentQuoteRequestId,
          SuggestionStatus.Accepted,
          {
            finalQuotePrice: _.round(quote.finalPrice),
            finalMargin: _.round(quote.profit),
            marginType: quote.profitType,
            finalCarrierCost: _.round(quote.carrierCost),
            carrierCostType: CarrierCostType.Flat,
            customerExternalTMSId: getValues('customerName'),
            quoteConfirmationStatus: QuoteConfirmationStatus.UserConfirmed,
            quoteTerminatingAction: terminatingAction,
          },
          laneSelectedQuickQuoteIds[quote.laneIndex]
        );

        if (result.isErr()) {
          captureException(result.error, {
            functionName: 'handleTerminatingAction',
          });
        }
      });

      await Promise.all(updatePromises);

      // Mark processed suggestions Completed! and advance to next pending QQ
      const completedIds = quotesWithResults
        .map((quote) => quote?.suggestion?.id ?? quote?.parentQuoteRequestId)
        .filter((id): id is number => typeof id === 'number' && id > 0);

      if (completedIds.length > 0) {
        setCurrentState((prevState) => ({
          ...prevState,
          ...applyCompleteKitSuggestions(
            prevState,
            completedIds,
            SuggestionPipelines.QuickQuote
          ),
        }));
      }
    } catch (error) {
      captureException(error, {
        functionName: 'BatchQuoteForm.handleTerminatingAction',
      });
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      const content = isHTMLContent(draftResponse)
        ? createRichTextContent(draftResponse)
        : decodeHtmlEntities(draftResponse);
      const success = await copyToClipboard(content, {
        joditEditorRef: draftResponseJoditRef,
        wrapInHTML: true,
      });
      if (success) {
        setHasCopiedDraftResponse(true);

        // Update quote request suggestion with the status and the final quote the user uses.
        handleTerminatingAction(QuoteTerminatingAction.Copy);

        setTimeout(() => setHasCopiedDraftResponse(false), 2000);
      }
    } catch (error) {
      captureException(error, { functionName: 'handleCopyToClipboard' });

      toast({
        description: 'Failed to copy to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const getLatestDraftResponse = useCallback(() => {
    const editorValue = draftResponseJoditRef.current?.value;

    return typeof editorValue === 'string' ? editorValue : draftResponse;
  }, [draftResponse]);

  const runBatchQuoteReply = async (action: QuoteReplyAction) => {
    const isSend = action === QuoteReplyAction.Send;
    if (isSend ? !canSendReply : !canDraftReply) {
      return;
    }

    const latestDraftResponse = getLatestDraftResponse();

    if (!latestDraftResponse.trim()) {
      return;
    }

    if (draftToRecipients.length === 0) {
      toast({
        description: isSend
          ? 'Add at least one To recipient before sending the quote reply.'
          : 'Add at least one To recipient before creating a draft reply.',
        variant: 'warning',
      });
      return;
    }

    if (latestDraftResponse !== draftResponse) {
      setDraftResponse(latestDraftResponse);
    }

    // Jodit content is already HTML; pass directly to mail client
    const formattedDraftBody = isHTMLContent(latestDraftResponse)
      ? latestDraftResponse.trim()
      : latestDraftResponse.trim().replace(/\n/g, '<br>');

    await replyWithMailClient({
      action,
      reply: {
        threadItemId,
        draftBody: formattedDraftBody,
        replyMode: DraftReplyMode.ReplyAll,
        to: draftToRecipients,
        cc: draftCc,
        bcc: draftBcc,
      },
      setLoading: setLoadingDraftReply,
      // Update quote request suggestion with the status and the final quote the user uses.
      onReplied: () => {
        void handleTerminatingAction(QuoteTerminatingAction.Reply);
      },
      functionName: isSend ? 'handleSendResponse' : 'handleDraftResponse',
    });
  };

  const handleDraftResponse = () => {
    void runBatchQuoteReply(QuoteReplyAction.Draft);
  };

  const handleSendResponse = () => {
    void runBatchQuoteReply(QuoteReplyAction.Send);
  };

  const {
    customers,
    isLoading: customersLoading,
    refreshCustomers: handleRefreshCustomers,
    resetCustomerSearch: handleResetCustomerSearch,
    customerSearch: handleCustomerSearch,
  } = useCustomers(tmsIntegrations, {
    toast,
    suggestedCustomerId: (suggestions[0]?.suggested as QuoteChanges)
      ?.customerExternalTMSID,
  });

  // Generate batch draft response with proper price calculation
  const generateBatchDraftResponse = useCallback(() => {
    const quotesWithResults = results
      .map((result, index) => {
        if (!result?.response) return null;

        const formData = getValues(`quotes.${index}`);

        // Get the actual calculated price from the lane card if available
        const laneCalculatedData = laneCalculatedPricesRef.current.get(index);

        let finalPrice: number;
        let maxDistance: number;
        let fuelEstimate: number;

        if (laneCalculatedData) {
          // Use the actual calculated price from the lane card
          finalPrice = laneCalculatedData.finalPrice;
          maxDistance = laneCalculatedData.maxDistance;
          fuelEstimate = laneCalculatedData.fuelEstimate;
        } else {
          // Fallback to basic calculation if lane data not available
          const carrierCost = result.cards?.length
            ? result.cards.reduce((prev, curr) =>
                prev.cost < curr.cost ? prev : curr
              ).cost
            : 0;

          maxDistance =
            result.response.quotes?.reduce(
              (max, current) =>
                current.distance > max.distance ? current : max,
              { distance: 0 }
            ).distance || 0;

          const profit = formData.profit || 0;
          const profitType = formData.profitType || ProfitType.Percentage;

          const { flatCarrierCost, finalProfit } = calculatePricing(
            carrierCost,
            CarrierCostType.Flat,
            profit,
            profitType,
            maxDistance
          );

          finalPrice = flatCarrierCost + finalProfit;
          fuelEstimate = 0;
        }

        // Get the price format for this lane, defaulting to Flat if not found
        const lanesPriceFormat =
          laneCurrentPriceFormatsRef.current.get(index) || CarrierCostType.Flat;

        return {
          quoteNumber: index + 1,
          // Multi-stop aware route string: "City, ST -> City, ST" for 2 stops,
          // or "City, ST -> City, ST -> City, ST" for 3+ stops. Mirrors the
          // collapsed batch quote card so the email reflects the full lane.
          route: formatStopsDisplay(result.response.stops ?? []),
          transportType: getEquipmentDisplayLabel(
            formData.subTransportType,
            formData.transportType,
            subTransportTypeOptions,
            transportTypeOptions
          ),
          price: formatPriceForDraft(
            finalPrice,
            maxDistance,
            fuelEstimate,
            lanesPriceFormat
          ),
          rawPrice: finalPrice,
        };
      })
      .filter(Boolean);

    if (quotesWithResults.length === 0) return '';

    // Get the actual customer display name instead of externalTMSID
    const customerId = getValues('customerName');
    const watchedCustomer = watchedCustomerName;

    let customerDisplayName = '';
    const customerIdToLookup = customerId || watchedCustomer;

    if (customerIdToLookup && customers?.length) {
      const foundCustomer = customers.find(
        (customer) => customer.externalTMSID === customerIdToLookup
      );
      if (foundCustomer?.name) {
        customerDisplayName = foundCustomer.name;
      }
    }

    const templateData = {
      customerName: customerDisplayName || '',
      hasCustomer: !!customerDisplayName,
      quotes: quotesWithResults,
      hasTotal: quotesWithResults.length > 1,
    };

    const renderedTemplate = Mustache.render(
      BATCH_QUOTE_TEMPLATE,
      templateData
    );
    return decodeHtmlEntities(renderedTemplate);
  }, [
    results,
    customers,
    getValues,
    watchedCustomerName,
    subTransportTypeOptions,
    transportTypeOptions,
  ]);

  // Reset user edited flag only when quote inputs actually change.
  // This avoids unrelated async updates (for example customer list loading) from
  // clearing the edit-protection while the user is typing.
  const previousDraftInputsRef = useRef({
    watchedQuotes,
    watchedCustomerName,
  });
  useEffect(() => {
    const previous = previousDraftInputsRef.current;
    const hasQuoteInputsChanged =
      !_.isEqual(previous.watchedQuotes, watchedQuotes) ||
      previous.watchedCustomerName !== watchedCustomerName;

    if (hasQuoteInputsChanged && results.some((result) => result !== null)) {
      resetUserEditedDraft();
    }

    previousDraftInputsRef.current = {
      watchedQuotes,
      watchedCustomerName,
    };
  }, [watchedQuotes, watchedCustomerName, results, resetUserEditedDraft]);

  // Force draft regeneration when customers data becomes available
  useEffect(() => {
    if (
      results.some((result) => result !== null) &&
      customers?.length &&
      !userEditedDraftRef.current
    ) {
      const newDraft = generateBatchDraftResponse();
      setDraftResponse(textToHtml(newDraft));
    }
  }, [customers, results, generateBatchDraftResponse]);

  // Update draft response when results, price format, or form values change
  useEffect(() => {
    if (results.some((result) => result !== null)) {
      const newDraft = generateBatchDraftResponse();
      // Only update the draft if the user hasn't manually edited the editor
      if (!userEditedDraftRef.current) {
        setDraftResponse(textToHtml(newDraft));
      }
    }
  }, [results, watchedQuotes, generateBatchDraftResponse]);

  // Ref to store setter functions for carrier selection per lane
  const carrierSetterMapRef = useRef(
    new Map<number, (carrier: SelectedQuoteType) => void>()
  );

  // Ref to store the actual calculated prices from each lane
  const laneCalculatedPricesRef = useRef(
    new Map<
      number,
      { finalPrice: number; maxDistance: number; fuelEstimate: number }
    >()
  );

  // Ref to store price format setter functions per lane
  const priceFormatSetterMapRef = useRef(
    new Map<number, (priceFormat: CarrierCostType | 'Both') => void>()
  );

  // Ref to store current price format for each lane
  const laneCurrentPriceFormatsRef = useRef(
    new Map<number, CarrierCostType | 'Both'>()
  );

  const registerCarrierSetter = (
    laneIndex: number,
    setter: (carrier: SelectedQuoteType) => void
  ) => {
    if (setter) {
      carrierSetterMapRef.current.set(laneIndex, setter);
    } else {
      carrierSetterMapRef.current.delete(laneIndex);
    }
  };

  const registerPriceCallback = (
    laneIndex: number,
    priceData: Maybe<{
      finalPrice: number;
      maxDistance: number;
      fuelEstimate: number;
    }>
  ) => {
    if (priceData) {
      laneCalculatedPricesRef.current.set(laneIndex, priceData);
    } else {
      laneCalculatedPricesRef.current.delete(laneIndex);
    }

    // Trigger draft regeneration when prices change
    if (
      results.some((result) => result !== null) &&
      !userEditedDraftRef.current
    ) {
      const newDraft = generateBatchDraftResponse();
      setDraftResponse(textToHtml(newDraft));
    }
  };

  const registerPriceFormatSetter = (
    laneIndex: number,
    setter: (priceFormat: CarrierCostType | 'Both') => void
  ) => {
    if (setter) {
      priceFormatSetterMapRef.current.set(laneIndex, setter);
      // Initialize with default format if not already set
      if (!laneCurrentPriceFormatsRef.current.has(laneIndex)) {
        laneCurrentPriceFormatsRef.current.set(laneIndex, CarrierCostType.Flat);
      }
    } else {
      priceFormatSetterMapRef.current.delete(laneIndex);
      laneCurrentPriceFormatsRef.current.delete(laneIndex);
    }
  };

  const handlePriceFormatChange = (
    laneIndex: number,
    priceFormat: CarrierCostType | 'Both'
  ) => {
    // Update the current price format tracking
    laneCurrentPriceFormatsRef.current.set(laneIndex, priceFormat);

    // Trigger draft regeneration when price format changes
    if (
      results.some((result) => result !== null) &&
      !userEditedDraftRef.current
    ) {
      const newDraft = generateBatchDraftResponse();
      setDraftResponse(textToHtml(newDraft));
    }
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit} className='space-y-3' ref={formContainerRef}>
        <TooltipProvider>
          <div className='mb-3'>
            <Typography variant='body-xs' className='text-neutral-600'>
              {suggestions.length > 1
                ? 'Review and get quotes for all lanes below.'
                : 'Quote multiple lanes at once.'}
            </Typography>
          </div>

          {/* Customer input */}
          {tmsIntegrations &&
            tmsIntegrations.length > 0 &&
            isCustomerSupportedTMS(tmsIntegrations) && (
              <div className='mb-3'>
                <RHFDebounceSelect
                  required={false}
                  name='customerName'
                  label='Customer'
                  control={control}
                  errors={errors}
                  data={customers}
                  isLoading={customersLoading}
                  showSearchParamDropdown={false}
                  refreshHandler={handleRefreshCustomers}
                  resetOptionsHandler={handleResetCustomerSearch}
                  fetchOptions={handleCustomerSearch}
                  mapOptions={mapCustomerToSelectOptions}
                  disabled={results.length > 0}
                />
              </div>
            )}

          {fields.length > 0 && (
            <Flex
              direction='col'
              gap='xs'
              className='mb-4 border-t border-neutral-400 pt-3'
            >
              <Flex justify='between' align='center' className='w-full'>
                <Flex align='center' gap='md'>
                  <label className='flex items-center gap-2 text-xs text-neutral-600 cursor-pointer'>
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                    />
                    <span className='font-medium'>
                      {isAllSelected ? 'Deselect All' : 'Select All'}
                    </span>
                  </label>
                </Flex>
                {hasSelection ? (
                  <>
                    {showBatchEdit ? (
                      <span className='text-xs text-neutral-500'>
                        Editing {selectedLanes.size}{' '}
                        {results.length > 0 ? 'quote' : 'lane'}
                        {selectedLanes.size > 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className='text-xs text-neutral-500'>
                        {selectedLanes.size} of {fields.length} selected
                      </span>
                    )}
                  </>
                ) : (
                  <span className='text-xs text-neutral-500'>
                    Select {results.length > 0 ? 'quotes' : 'lanes'} to edit
                  </span>
                )}
              </Flex>

              {expandableLanesCount > 0 && (
                <Flex align='center' justify='end' className='mt-1.5'>
                  <button
                    type='button'
                    onClick={handleToggleExpandAll}
                    className='flex items-center gap-0.5 text-xs text-info-600 font-medium hover:underline focus:outline-none'
                  >
                    {allLanesExpanded ? (
                      <>
                        <ChevronUpIcon className='w-4 h-4' />
                        <Typography variant='body-sm'>
                          Collapse All Quotes
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ChevronDownIcon className='w-4 h-4' />
                        <Typography variant='body-sm'>
                          Expand All Quotes
                        </Typography>
                      </>
                    )}
                  </button>
                </Flex>
              )}

              <BatchQuoteEdit
                selectedLanes={selectedLanes}
                showBatchEdit={showBatchEdit}
                hasResults={results.length > 0}
                transportTypeOptions={transportTypeOptions}
                subTransportTypeOptions={subTransportTypeOptions}
                isSubTransportTypesEnabled={useGranularEquipmentSelect}
                lastBatchEditMargin={lastBatchEditMargin}
                onInitiateBatchEdit={handleInitiateBatchEdit}
                onCancelBatchEdit={handleCancelBatchEdit}
                onApplyBatchEdit={handleApplyBatchEdit}
                isTMSLaneHistoryEnabled={isTMSLaneHistoryEnabled}
              />
            </Flex>
          )}

          <Flex
            direction='col'
            gap='md'
            align='center'
            justify='center'
            className='w-full'
          >
            {fields.map((field, index) => (
              <div key={field.id} data-lane-card className='w-full'>
                <BatchQuoteInputCard
                  index={index}
                  control={control}
                  transportTypeOptions={transportTypeOptions}
                  subTransportTypeOptions={subTransportTypeOptions}
                  isSubTransportTypesEnabled={useGranularEquipmentSelect}
                  onRemove={() => handleRemoveQuote(index)}
                  result={results[index] ?? undefined}
                  laneHistoryResult={laneHistoryResults[index] ?? undefined}
                  laneHistoryFromServiceResult={
                    laneHistoryFromServiceResults[index] ?? undefined
                  }
                  setValue={setValue}
                  isSelected={selectedLanes.has(index)}
                  onSelect={(isSelected) => handleSelectLane(index, isSelected)}
                  quickQuoteConfig={quickQuoteConfig}
                  isExpanded={expandedLanes.has(index)}
                  onToggleExpand={() => handleToggleExpandLane(index)}
                  registerCarrierSetter={registerCarrierSetter}
                  registerPriceCallback={registerPriceCallback}
                  registerPriceFormatSetter={registerPriceFormatSetter}
                  onPriceFormatChange={handlePriceFormatChange}
                  isTMSLaneHistoryEnabled={isTMSLaneHistoryEnabled}
                  isQuoteLaneHistoryEnabled={isQuoteLaneHistoryEnabled}
                  isGetLaneRateFromServiceEnabled={
                    isGetLaneRateFromServiceEnabled
                  }
                  onRetryQuote={handleRetryQuote}
                  isRetrying={retryingQuotes.has(index)}
                  onSelectedQuoteIdChange={onSelectedQuoteIdChange}
                  onLaneTerminatingAction={onLaneTerminatingAction}
                />
              </div>
            ))}

            {fields.length === 0 && (
              <Flex
                direction='col'
                align='center'
                justify='center'
                className='py-8 px-4 border-2 border-neutral-400 rounded-lg bg-neutral-50'
                gap='sm'
              >
                <Flex
                  align='center'
                  justify='center'
                  className='mx-auto h-12 w-12 rounded-full bg-info-100'
                >
                  <Truck className='h-5 w-5 text-info-600' />
                </Flex>
                <div>
                  <Typography
                    variant='h6'
                    weight='medium'
                    className='text-neutral-900'
                  >
                    No lanes to quote
                  </Typography>
                  <Typography
                    variant='body-xs'
                    className='text-neutral-500 mt-1'
                  >
                    Add lanes to get quotes for multiple routes at once
                  </Typography>
                </div>
                <Flex gap='sm' justify='center'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={handleAddLane}
                    className='text-xs px-3 py-1.5 h-8 text-neutral-900 hover:border-neutral-600 hover:bg-neutral-200'
                    buttonNamePosthog={ButtonNamePosthog.BatchQuoteAddLane}
                    logProperties={{
                      ...logProperties,
                      state: 'with_no_lanes',
                    }}
                  >
                    <Flex align='center' gap='xs'>
                      <PlusIcon className='w-3 h-3' />
                      Add Lane
                    </Flex>
                  </Button>
                </Flex>
                {suggestions.length > 1 && (
                  <>
                    <Button
                      className='text-sm border-info-300 hover:border-info-500 bg-linear-to-br from-neutral-50 to-info-100/50 text-info-600'
                      onClick={() => {
                        // Repopulate form with quote request suggestions
                        reset(memoizedDefaultValues);
                      }}
                      type='button'
                      buttonNamePosthog={
                        ButtonNamePosthog.BatchQuoteAddLanesUsingAI
                      }
                      variant='outline'
                      size='sm'
                    >
                      <SparklesIcon className='w-3 h-3 mr-1' />
                      <Typography variant='body-xs'>
                        Add lanes using AI suggestions
                      </Typography>
                    </Button>
                  </>
                )}
              </Flex>
            )}

            {fields.length > 0 && (
              <>
                <div className='pb-3'>
                  <Flex gap='sm' justify='center'>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      onClick={handleAddLane}
                      className='text-xs px-3 py-1.5 tracking-wide text-neutral-600 border border-transparent hover:border-neutral-600 hover:bg-neutral-200'
                      buttonNamePosthog={ButtonNamePosthog.BatchQuoteAddLane}
                      logProperties={{
                        ...logProperties,
                        state: 'with_lanes',
                      }}
                    >
                      <Flex align='center' gap='xs'>
                        <PlusIcon className='w-3 h-3' />
                        Add Lane
                      </Flex>
                    </Button>
                  </Flex>
                </div>

                <Button
                  type='submit'
                  className='w-full'
                  disabled={isSubmitButtonDisabled}
                  buttonNamePosthog={ButtonNamePosthog.GetBatchQuote}
                  logProperties={logProperties}
                >
                  {isSubmitting ? <ButtonLoader /> : ButtonText.GetBatchQuote}
                </Button>
              </>
            )}

            {/* Show draft response if Drumkit running on email platform (Gmail, Outlook, Front) */}
            {results.length > 0 && (
              <div data-testid='batch-quote-results'>
                {supportsDraftResponse(drumkitPlatform) && (
                  <div className='mt-4'>
                    <Flex justify='between' align='center' className='mb-3'>
                      <Typography
                        variant='h5'
                        weight='semibold'
                        className='text-neutral-800'
                      >
                        Draft Response
                      </Typography>
                      <div className='relative'>
                        <Button
                          buttonNamePosthog={
                            ButtonNamePosthog.BatchQuoteCopyQuoteToClipboard
                          }
                          logProperties={logProperties}
                          className={cn(
                            'h-6 w-6 p-0 border-none',
                            hasCopiedDraftResponse
                              ? 'cursor-default'
                              : 'cursor-pointer'
                          )}
                          variant='ghost'
                          type='button'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            !hasCopiedDraftResponse && handleCopyToClipboard();
                          }}
                        >
                          {hasCopiedDraftResponse ? (
                            <Tooltip open={true}>
                              <TooltipTrigger asChild>
                                <CheckIcon className='h-4 w-4' />
                              </TooltipTrigger>
                              <TooltipContent>Copied!</TooltipContent>
                            </Tooltip>
                          ) : (
                            <CopyIcon className='h-4 w-4' />
                          )}
                        </Button>
                      </div>
                    </Flex>

                    <DraftRecipientFields
                      to={draftToRecipients}
                      cc={draftCc}
                      bcc={draftBcc}
                      onToChange={setDraftTo}
                      onCcChange={setDraftCc}
                      onBccChange={setDraftBcc}
                    />

                    <JoditEditor
                      value={draftResponse}
                      config={{
                        ...getJoditEditorConfig({
                          refToUpdateAfterInit: draftResponseJoditRef,
                        }),
                        placeholder: 'Quote results will appear here...',
                        minHeight: 192,
                      }}
                      // NOTE: Do not use `onChange` to mark the draft as
                      // user-edited. Jodit fires `onChange` for programmatic
                      // value updates too, which would flip
                      // `userEditedDraftRef` on the very first draft
                      // generation (run before per-lane prices register via
                      // `registerPriceCallback`), blocking subsequent
                      // regenerations and leaving the email stuck on the
                      // fallback $0 prices until the next submit. Mirror the
                      // single Quick Quote `DraftResponseSection` and rely
                      // on `onBlur` to capture user edits.
                      onBlur={(value: string) => {
                        setDraftResponse(value);
                        markUserEditedDraft();
                      }}
                    />

                    <OutlookQuoteReplyActions
                      drumkitPlatform={drumkitPlatform}
                      isSharedMailbox={isSharedMailbox}
                      isOutlookReply={isOutlookReply}
                      loadingDraftReply={loadingDraftReply}
                      disabled={draftToRecipients.length === 0}
                      onSend={handleSendResponse}
                      onDraft={handleDraftResponse}
                      onCopy={handleCopyToClipboard}
                      logProperties={logProperties}
                      draftButtonNamePosthog={
                        ButtonNamePosthog.BatchQuoteCreateDraftReply
                      }
                      addToCurrentDraftButtonNamePosthog={
                        ButtonNamePosthog.BatchQuoteAddReplyToCurrentDraft
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </Flex>
        </TooltipProvider>
      </form>
    </FormProvider>
  );
};

export default BatchQuoteForm;
